import angular from 'angular';
import Raven from 'raven-js';
import { IS_TEST, ENVIRONMENT } from '../../../../environment';

export default angular
  .module('services.error-reporters.raven', [])
  .config(RavenConfig)
  .run(RavenRun);

const { url: API_URL, version } = ENVIRONMENT.endpoints.api;
// We expect that these responses from the api are benign
const WARNING_RESPONSE_STATUSES = [-1, 401, 404, 418];
const RESPONSES_TO_IGNORE = [
  {
    httpResponseStatus: 404,
    apiRequestEndpoint: `${API_URL}/${version}/users/emails`,
  },
  {
    httpResponseStatus: 418,
    apiRequestEndpoint: `${API_URL}/${version}/sessions`,
    pathname: '/sign-in',
  },
  {
    httpResponseStatus: 401,
    apiRequestEndpoint: `${API_URL}/${version}/sessions`,
    pathname: '/sign-in',
  },
];
const INVITE_ENDPOINT = new RegExp(`${API_URL}\\/${version}\\/invites\\/.+`);

// Sentry already scrubs passwords server-side, but might as well do it client
// side to avoid sending plaintext passwords to a 3rd party.
export const omitPassword = variables =>
  JSON.parse(JSON.stringify(variables), (key, value) =>
    key === 'password' ? '[FILTERED]' : value
  );

// Angular stringifies unhandled exceptions before passing them to the exception handler.
// https://github.com/angular/angular.js/blob/26a5779cddf70944b7548e3a6410d35237a516e5/src/ng/q.js#L387
// Here we try to restore the underlying object structure (e.g. error action), if any by looking for the
// potential start of an object and parsing the string from there
function parseUnhandledException(exception) {
  try {
    return omitPassword(JSON.parse(exception.substr(exception.indexOf('{'))));
  } catch (e) {
    return exception;
  }
}

function RavenConfig($provide) {
  $provide.decorator('$exceptionHandler', function($delegate) {
    return function(exception, cause) {
      if (Raven.isSetup()) {
        const data = { cause };

        if (!(exception instanceof Error)) {
          const _exception = parseUnhandledException(exception);
          const error = new Error(JSON.stringify(_exception));

          // Send $http rejections to Sentry in a meaningful format
          if (
            _exception.type === 'upd/REQUEST_RESULT' &&
            _exception.meta &&
            _exception.meta.response &&
            _exception.meta.request
          ) {
            const httpResponseStatus = _exception.meta.response.status;
            const apiRequestEndpoint = _exception.meta.request.payload.url;

            error.name = `HttpResponseError: ${httpResponseStatus} Response`;
            error.message = apiRequestEndpoint;

            // Group errors with the same response and endpoint:
            // https://docs.sentry.io/learn/rollups/#customize-grouping-with-fingerprints
            const isInvite = INVITE_ENDPOINT.test(apiRequestEndpoint);
            data.fingerprint = [
              String(httpResponseStatus),
              isInvite ? 'invite' : apiRequestEndpoint,
            ];

            if (WARNING_RESPONSE_STATUSES.includes(httpResponseStatus)) {
              data.level = 'warning';
            } else {
              data.level = 'error';
            }
            // Surface the response status, request url, and location info as Sentry tags
            data.tags = {
              httpResponseStatus,
              apiRequestEndpoint,
              hostname: window.location.hostname,
              pathname: window.location.pathname,
            };
            // Send the unaltered exception to Sentry just in case
            data.extra = _exception;
          }

          Raven.captureException(error, data);
        } else {
          Raven.captureException(exception, data);
        }
      }

      $delegate(exception, cause);
    };
  });
}

const shouldIgnoreResponse = data => ignore =>
  Object.keys(ignore).every(key => data.tags[key] === ignore[key]);

function RavenRun() {
  // Don't log errors while testing or if Raven Integration is disabled
  if (!IS_TEST && !ENVIRONMENT?.integrations?.disableRaven) {
    window.Raven = Raven;
    Raven.config(ENVIRONMENT.keys.sentry, {
      environment: ENVIRONMENT.name,
      maxMessageLength: 1000,
      release: __COMMIT_HASH__,
      shouldSendCallback: data => {
        if (!data || !data.tags) {
          return true;
        }

        // These errors are expected in regular use; don't even send these to Sentry
        return !RESPONSES_TO_IGNORE.some(shouldIgnoreResponse(data));
      },
    }).install();
  }
}
